import { useState } from 'react';
import { Modal, ModalFooter } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { verify2FA } from '../../api/two-factor';
import type { User } from '../../api/auth';

interface TwoFactorVerifyModalProps {
  isOpen: boolean;
  challengeToken: string;
  expiresAt: string;
  onSuccess: (user: User, csrfToken: string) => void;
  onCancel: () => void;
  onUseRecoveryCode: () => void;
}

export function TwoFactorVerifyModal({
  isOpen,
  challengeToken,
  expiresAt,
  onSuccess,
  onCancel,
  onUseRecoveryCode,
}: TwoFactorVerifyModalProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isExpired = new Date(expiresAt) < new Date();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await verify2FA(challengeToken, code);
      onSuccess(response.user, response.csrfToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError(null);
    onCancel();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Two-factor authentication" size="sm">
      <form onSubmit={handleSubmit} className="space-y-6">
        {isExpired ? (
          <Alert variant="error">
            Your verification session has expired. Please log in again.
          </Alert>
        ) : (
          <>
            <p className="text-body-sm text-text-secondary">
              Enter the 6-digit code from your authenticator app.
            </p>

            {error && (
              <Alert variant="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Input
              label="Verification code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoFocus
              required
              disabled={isExpired}
            />
          </>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={onUseRecoveryCode}
            className="text-body-sm text-text-tertiary hover:text-text-primary underline"
            disabled={isExpired}
          >
            Use a recovery code instead
          </button>
        </div>

        <ModalFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={code.length !== 6 || isLoading || isExpired}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
